import { useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumbs from '@/components/Breadcrumbs';
import EngagementLinks from '@/components/EngagementLinks';
import FragmentLink from '@/components/FragmentLink';
import { inquiryHref } from '@/lib/engagements';
import type { Crumb } from '@/components/SEO';
import './service-experience.css';

type ServiceKind = 'upgrades' | 'migrations' | 'support';

const JOURNEYS = {
  upgrades: [
    { label: 'Assess', title: 'Start with the estate you have.', body: 'Map versions, customizations and the invoice journey: recognition, account coding, approvals and document access. Separate platform readiness from the application changes required for 14c.', link: '/tools/wcc-component-inventory', action: 'Explore the component inventory' },
    { label: 'Rehearse', title: 'Prove the move before cutover.', body: 'Use representative invoices to test WFR recognition rules and exceptions, non-PO account distributions and approvals. Rehearse SOA workflow changes alongside the infrastructure upgrade.', link: '/upgrades/clone-webcenter-content-for-test-upgrade', action: 'Read the rehearsal guide' },
    { label: 'Validate', title: 'Make acceptance part of the plan.', body: 'Platform checks and application tests need separate evidence. Check documents, permissions and the agreed workflow closeout or transfer, then confirm acceptance with the people who use the system.', link: '/upgrades/webcenter-content-upgrade-validation-checklist', action: 'Read the validation checklist' },
  ],
  migrations: [
    { label: 'Retain', title: 'Some things should stay.', body: 'Identify the records and document links to retain. Decide whether SOA work finishes before cutover or requires an in-flight plan, separately from the status of invoices in the ERP.', link: '/migrations/webcenter-records-retention-modernization', action: 'Explore the retention decisions' },
    { label: 'Move', title: 'Move the whole dependency chain.', body: 'Plan content transfer and reconcile workflow tasks, statuses and document links at cutover. For OCI, test clustering, session continuity, Coherence caching and failover.', link: '/migrations/webcenter-content-repository-to-oci', action: 'Explore the move to OCI' },
    { label: 'Rebuild', title: 'Change the part that needs changing.', body: 'Connect the invoice process to a new ERP instance: E-Business Suite, PeopleSoft or JD Edwards. Rework account coding, approvals and document access against the target integration requirements.', link: '/contact?topic=migration', action: 'Discuss your ERP integration' },
  ],
  support: [
    { label: 'Review', title: 'A clear view of the whole estate.', body: 'Trace issues across recognition, forms, workflows and repository access. Document the dependencies, recurring exceptions and priorities alongside versions and patch levels.', link: '/estate-review', action: 'Explore the Estate Review' },
    { label: 'Maintain', title: 'Keep the day-to-day work moving.', body: 'Resolve WFR recognition exceptions, non-PO coding issues, stalled approvals and document-access failures. Patches, fixes and small changes stay visible in a shared work list.', link: '#support-shapes', action: 'See the standing retainer' },
    { label: 'Change', title: 'Give a major move its own scope.', body: 'Scope an upgrade, workflow refactor or ERP integration as a defined engagement. Include application tests and an agreed approach to workflow closeout and invoice reconciliation.', link: '#support-shapes', action: 'Compare defined engagements' },
  ],
};

function Plate({ x, y, title, detail, selected = false }: { x: number; y: number; title: string; detail?: string; selected?: boolean }) {
  return (
    <g transform={`translate(${x} ${y})`} className={`service-plate ${selected ? 'is-selected' : ''}`}>
      <path d="M0 14 22 0 160 0 138 14Z" className="service-plate-top" />
      <path d="M138 14 160 0 160 72 138 86Z" className="service-plate-side" />
      <path d="M0 14 138 14 138 86 0 86Z" className="service-plate-face" />
      <text x="13" y="43" className="service-svg-title">{title}</text>
      {detail && <text x="13" y="64" className="service-svg-detail">{detail}</text>}
    </g>
  );
}

function EstateDiagram({ kind, selected }: { kind: ServiceKind; selected: number }) {
  const grid = `service-grid-${kind}`;
  return (
    <svg className="service-diagram" viewBox="0 0 580 365" aria-hidden="true" focusable="false">
      <defs>
        <pattern id={grid} width="28" height="28" patternUnits="userSpaceOnUse">
          <path d="M28 0H0V28" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.14" />
        </pattern>
      </defs>
      <rect x="5" y="5" width="570" height="350" fill={`url(#${grid})`} />
      {kind === 'upgrades' && <>
        <path className="service-connector" d="M120 265H474M120 265V242M298 265V242M474 265V242" />
        <path className="service-connector" d="M160 142H208M338 142H386" />
        <text x="24" y="42" className="service-svg-kicker">01 / CURRENT</text>
        <text x="204" y="42" className="service-svg-kicker">02 / REHEARSAL</text>
        <text x="384" y="42" className="service-svg-kicker">03 / TARGET</text>
        <Plate x={24} y={150} title="Dependencies" detail="Map & inspect" selected={selected === 0} />
        <Plate x={24} y={86} title="12c estate" detail="Content & workflow" selected={selected === 0} />
        <Plate x={204} y={150} title="Test evidence" detail="Compare & correct" selected={selected === 1} />
        <Plate x={204} y={86} title="Test environment" detail="Rehearse the move" selected={selected === 1} />
        <Plate x={384} y={150} title="Acceptance" detail="Validate & hand over" selected={selected === 2} />
        <Plate x={384} y={86} title="14c estate" detail="Component paths" selected={selected === 2} />
        <circle cx={[120, 298, 474][selected]} cy="265" r="5" className="service-diagram-marker" />
        <text x="290" y="312" textAnchor="middle" className="service-svg-detail">The dependencies travel with the program.</text>
      </>}
      {kind === 'migrations' && <>
        <path className={`service-route ${selected === 0 ? 'is-selected' : ''}`} d="M181 170H239V67H370" />
        <path className={`service-route ${selected === 1 ? 'is-selected' : ''}`} d="M181 170H370" />
        <path className={`service-route ${selected === 2 ? 'is-selected' : ''}`} d="M181 170H239V273H370" />
        <Plate x={29} y={157} title="Integrations" detail="Process dependencies" />
        <Plate x={29} y={93} title="Your estate" detail="Content & workflows" />
        <circle cx="239" cy="170" r="7" className="service-diagram-marker" />
        <text x="211" y="329" className="service-svg-kicker">DECIDE BY WORKLOAD</text>
        <Plate x={370} y={17} title="Retain" detail="Keep what works" selected={selected === 0} />
        <Plate x={370} y={120} title="Move" detail="Change the platform" selected={selected === 1} />
        <Plate x={370} y={223} title="Rebuild" detail="Change the process" selected={selected === 2} />
        <text x="291" y="59" className="service-svg-kicker">01</text>
        <text x="291" y="161" className="service-svg-kicker">02</text>
        <text x="291" y="264" className="service-svg-kicker">03</text>
      </>}
      {kind === 'support' && <>
        <ellipse cx="295" cy="198" rx="185" ry="119" className="service-orbit" />
        <ellipse cx="295" cy="198" rx="112" ry="72" className="service-orbit service-orbit-inner" />
        <path className="service-connector" d="M285 108V169M170 245 237 214M428 245 356 214" />
        <path d="m280 139 5 6 5-6m-83 91 8-1-3-7m176 0-3 7 8 1" className="service-connector" />
        <Plate x={212} y={33} title="Review" detail="Findings & roadmap" selected={selected === 0} />
        <Plate x={35} y={227} title="Maintain" detail="Patch, resolve, advise" selected={selected === 1} />
        <Plate x={377} y={227} title="Change" detail="Upgrade or migrate" selected={selected === 2} />
        <path d="m241 163 54-31 54 31v61l-54 32-54-32z" className="service-estate-core" />
        <text x="295" y="187" textAnchor="middle" className="service-svg-title">Your</text>
        <text x="295" y="208" textAnchor="middle" className="service-svg-title">estate</text>
        <circle cx="295" cy="256" r="4" className="service-diagram-marker" />
      </>}
    </svg>
  );
}

interface ServiceExperienceProps {
  kind: ServiceKind;
  crumbs: Crumb[];
  label: string;
  title: string;
  lead: string;
  note: string;
}

export default function ServiceExperience({ kind, crumbs, label, title, lead, note }: ServiceExperienceProps) {
  const [selected, setSelected] = useState(0);
  const journey = JOURNEYS[kind];
  const item = journey[selected];
  return (
    <>
    <header className={`service-experience service-experience--${kind} on-forest`}>
      <div className="container">
        <div className="service-breadcrumb"><Breadcrumbs items={crumbs} /></div>
        <div className="service-hero-grid">
          <div className="service-introduction">
            <p className="label service-eyebrow">{label}</p>
            <h1>{title}</h1>
            <p className="service-lead">{lead}</p>
            <div className="service-actions">
              <Link to={inquiryHref(`/${kind}`)} className="btn-cream">Talk to an engineer <span aria-hidden="true">↗</span></Link>
              <FragmentLink hash="#guides-heading" className="service-text-link">Explore the guides <span aria-hidden="true">↓</span></FragmentLink>
            </div>
            <p className="service-intro-note">{note}</p>
          </div>
          <div className="service-explorer">
            <div className="service-drawing-heading"><span>THE WORK, MADE VISIBLE</span><span>ECM / {kind === 'upgrades' ? '01' : kind === 'migrations' ? '02' : '03'}</span></div>
            <EstateDiagram kind={kind} selected={selected} />
            <div className="service-explorer-controls" role="group" aria-label={`Explore the ${kind} approach`}>
              {journey.map((step, i) => (
                <button key={step.label} type="button" aria-pressed={selected === i} aria-controls={`service-detail-${kind}`} onClick={() => setSelected(i)}>
                  <span>0{i + 1}</span> {step.label}
                </button>
              ))}
            </div>
            <div id={`service-detail-${kind}`} className="service-explorer-detail" aria-live="polite" aria-atomic="true">
              <h2>{item.title}</h2>
              <p>{item.body}</p>
              {item.link.startsWith('#') ? <FragmentLink hash={item.link} className="service-text-link">{item.action} <span aria-hidden="true">↗</span></FragmentLink> : <Link to={item.link} className="service-text-link">{item.action} <span aria-hidden="true">↗</span></Link>}
            </div>
            <p className="service-diagram-note">Illustrative approach. The route depends on your estate.</p>
          </div>
        </div>
      </div>
    </header>
    <EngagementLinks current={`/${kind}`} />
    </>
  );
}
